import { computed, readonly, ref, watch } from 'vue'
import webapp from '@/services/webapp'
import { useLocalStorage } from '@vueuse/core'
import { useNow } from '@/composables/game/time'
import api from '@/services/api'
import router from '@/router'
import { RouteName } from '@/routes'
import { buildDropeeStorageKey, isDropeeStorageKey } from '@/config/config'
import { callApi } from '@/services/call-api'

function buildEmptyProfile() {
  return {
    id: 0,
    token: '',
    referralCode: '',
    photoUrl: '',
    firstName: '',
  }
}

const profile = useLocalStorage(buildDropeeStorageKey('auth'), buildEmptyProfile())
const connectedReferralCodes = useLocalStorage<string[]>(buildDropeeStorageKey('connectedReferralCodes.v2'), [])

export const apiToken = computed(() => profile.value.token)

export const useAuth = () => {
  return {
    profile: readonly(profile),
    deleteAccount: async () => {
      await api.deleteAccount()
      for (let i = 0; i++; i < localStorage.length) {
        const key = localStorage.key(i)
        if (!key || !isDropeeStorageKey(key)) continue
        localStorage.removeItem(key)
      }
      localStorage.clear()
      webapp.close()
    },
  }
}

async function generateToken() {
  const searchParams = new URLSearchParams(window.location.search)
  const impersonationToken = searchParams.get('impersonationToken')
  const startParam = webapp.getStartParam()
  const referrerCode = startParam?.startsWith('ref_') ? startParam.slice(4) : searchParams.get('rc')
  const utmSource = searchParams.get('utm_source')
  type AuthResponse = { error: string } | {
    id: number
    token: string
    referralCode: string
    firstName: string
    photoUrl: string
  }
  const response = searchParams.has('fakeCode') ?
    await callApi<AuthResponse>({
      path: '/api/game/telegram/fake',
      method: 'POST',
      body: {
        telegramUserId: Number(searchParams.get('id')),
        referrerCode: searchParams.get('referrerCode') || null,
        isPremium: webapp.isPremium,
        secretCode: searchParams.get('fakeCode'),
        coins: Number(searchParams.get('coins') || '0'),
        spins: Number(searchParams.get('spins') || '0'),
        utmSource: utmSource,
      },
    })
    : await callApi<AuthResponse>({
      path: '/api/game/telegram/me',
      method: 'POST',
      body: {
        initData: webapp.initData(),
        referrerCode: referrerCode ?? null,
        utmSource: utmSource,
        impersonationToken: impersonationToken,
      },
    })

  if ('error' in response) {
    throw new Error(response.error)
  }
  profile.value = {
    id: response.id,
    token: response.token,
    referralCode: response.referralCode,
    photoUrl: response.photoUrl,
    firstName: response.firstName,
  }
}

async function renewTokenIfNeeded(): Promise<boolean> {
  const hasFakeCode = window.location.search.includes('fakeCode')
  const hasImpersonationToken = window.location.search.includes('impersonationToken')
  if (!profile.value.token || hasFakeCode || hasImpersonationToken) {
    await generateToken()
    if (hasFakeCode) {
      await router.replace({ name: RouteName.HOME })
    }
    return true
  }

  const base64Url = profile.value.token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))

  const payload = JSON.parse(jsonPayload) as { exp: number, isFake?: boolean, player: { isPremium: boolean } }
  if ((payload.exp - 7200) * 1000 < Date.now() || (payload.player.isPremium !== webapp.isPremium && !payload.isFake)) {
    await generateToken()
    return true
  }

  return false
}

export const useAuthInit = () => {
  const { nowDateHourTimestamp } = useNow()
  watch(nowDateHourTimestamp, async () => {
    await renewTokenIfNeeded()
  })
}

const showFriendsConfirm = ref(false)
const friendToConfirmName = ref('')
let resolvePromise: ((value: boolean) => void) | undefined = undefined

export function useFriendConfirm() {
  const confirm = () => {
    if (resolvePromise){
      resolvePromise(true)
    }
    showFriendsConfirm.value = false
  }

  const cancel = () => {
    if (resolvePromise) {
      resolvePromise(false)
    }
    showFriendsConfirm.value = false
  }

  const doShowFriendsConfirm = (friendName: string): Promise<boolean> => {
    friendToConfirmName.value = friendName
    showFriendsConfirm.value = true

    return new Promise<boolean>((resolve) => {
      resolvePromise = resolve
    })
  }

  return { showFriendsConfirm, friendToConfirmName, doShowFriendsConfirm, confirm, cancel }
}

export function removeToken() {
  profile.value.token = ''
}

export async function initializeAuth() {
  await renewTokenIfNeeded()

  const startParam = webapp.getStartParam()
  if (startParam?.startsWith('ref_')) {
    const referralCode = startParam.slice(4)
    if (connectedReferralCodes.value.includes(referralCode)) return
    await api.addFriend(referralCode)
    connectedReferralCodes.value.push(referralCode)
    await router.push({ name: RouteName.FRIENDS })
  }
}
